import { isAxiosError } from "axios";
import { z } from "zod";
import type { translations } from "@/translations/translations";

const errorCodeSchema = z.enum([
  "VALIDATION_FAILED",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "PHRASE_SELECTION_STALE",
  "PHRASE_CURATION_ORDER_DUPLICATE",
  "PHRASE_WORKBOOK_UNSUPPORTED",
  "FILE_REQUIRED",
  "NO_SUPPORTED_PHRASES",
  "PHRASE_IMPORT_LIMIT_EXCEEDED",
  "FORMULA_INJECTION_DETECTED",
  "INTERNAL_SERVER_ERROR",
]);

const errorEnvelopeSchema = z
  .object({
    error: z.literal(true),
    status: z.number().int().min(400).max(599),
    message: errorCodeSchema,
    data: z.null(),
    meta: z
      .object({
        request_id: z.string().uuid(),
        served_at: z.string().datetime(),
        retryable: z.boolean().optional(),
        violations: z.array(z.string()).max(100).optional(),
      })
      .strict(),
  })
  .strict();

type ErrorMessageKey =
  keyof typeof translations.fr.admin.excelPhrases.serverErrors;

const messageKeyByCode = {
  VALIDATION_FAILED: "VALIDATION_FAILED",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  UNSUPPORTED_MEDIA_TYPE: "UNSUPPORTED_MEDIA_TYPE",
  PHRASE_SELECTION_STALE: "PHRASE_SELECTION_STALE",
  PHRASE_CURATION_ORDER_DUPLICATE: "PHRASE_CURATION_ORDER_DUPLICATE",
  PHRASE_WORKBOOK_UNSUPPORTED: "PHRASE_WORKBOOK_UNSUPPORTED",
  FILE_REQUIRED: "FILE_REQUIRED",
  NO_SUPPORTED_PHRASES: "NO_SUPPORTED_PHRASES",
  PHRASE_IMPORT_LIMIT_EXCEEDED: "PHRASE_IMPORT_LIMIT_EXCEEDED",
  FORMULA_INJECTION_DETECTED: "FORMULA_INJECTION_DETECTED",
  INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR",
} satisfies Record<z.infer<typeof errorCodeSchema>, ErrorMessageKey>;

export function excelPhraseErrorMessage(
  error: unknown,
  messages: typeof translations.fr.admin.excelPhrases.serverErrors,
): string {
  if (!isAxiosError(error)) return messages.DEFAULT;
  const parsed = errorEnvelopeSchema.safeParse(error.response?.data);
  if (!parsed.success) return messages.DEFAULT;
  return messages[messageKeyByCode[parsed.data.message]];
}

export function isPhraseSelectionStale(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const parsed = errorEnvelopeSchema.safeParse(error.response?.data);
  return parsed.success && parsed.data.message === "PHRASE_SELECTION_STALE";
}
